//done
import * as config from "../constants.js";
import {} from "./util.js";
import { updateUI } from "./game.js";
import { gameState, saveGameState } from "./gameState.js";
import { getLevelBasedCardColors, processCharacterHealthRegeneration } from "./character.js";
import { addNotification } from "./notifications.js";
import { blackoutModifier } from './blackout-core.js';
import { multiplayer } from "./multiplayer.js";
import { updateGridTeamDisplay, updateGridDisplay, updatePathDisplay, getRaidTeam, isContractorReserved, isContractorHired } from "./raid.js";
import { getSkillIcon, getTechniqueName, getSkillDisplayName, getAvatarSeed } from "./util.js";
import { generateContractorsCore, getContractorSynergyStatus } from "./contractor-core.js";
import { expectedRaidOutcome } from "./raidEconomics-core.js";


export function generateContractors() {
  // Calculate skill level range based on player level
  const playerLevel = gameState.character ? gameState.character.level : 1;
  const newContractors = generateContractorsCore(playerLevel);

  // Update both gameState and Alpine store
  if (Alpine.store("contractors")) {
    Alpine.store("contractors").update(newContractors);
  } else {
    // Fallback if Alpine isn't ready yet
    gameState.contractors = newContractors;
  }
}

// Create unified contractor card that works for all contexts
export function createContractorCard(member, context = "main", pathSkills = null, teamSkills = null) {
  //console.log('Creating contractor card for:', member, 'in context:', context);
  // Handle empty slot mode
  const isReadOnly = gameState.character && gameState.character.isMarketplace;
  const isEmpty = member === "empty";
  // Crewing is a PLANNING action. An empty slot goes on showing its Reserve
  // button once the raid is underway or finished, which offers to add somebody
  // to a team that has already left — reserving mid-raid cannot put them on it,
  // and reserving against a finished one is meaningless. The slot itself still
  // renders, because "you went in two-handed" is worth seeing on the way round.
  const raidStarted = !!gameState.currentRaid.raidInProgress || !!gameState.currentRaid.raidCompleted;
  const showReserveButton = !isReadOnly && !raidStarted && context === "empty-first";

  // Identity check, not type-sniffing: character ids are strings for created
  // characters but numeric in QA fixtures/legacy saves - type-based detection
  // rendered the player as a "Contractor" card (undefined% reward share)
  const isPlayer = !isEmpty && gameState.character != null && member.id === gameState.character.id;
  const isMarketplaceContractor = !isEmpty && member.id > 0 && !isPlayer;
  const faction = !isEmpty && (isPlayer ? config.factionSkills[member.faction] : config.factionSkills[member.faction]);

  // Determine member status
  const isHired = !isEmpty && !isPlayer && isContractorHired(member.id);
  const isReserved = !isEmpty && !isPlayer && isContractorReserved(member.id);
  const isInjured = !isEmpty && (member.injured || false);
  const raidCompleted = gameState.currentRaid.raidCompleted;

  // Synergy indicators: ⚡ = a partner device is already equipped on the
  // prospective team (synergy variants can fire on this skill's events);
  // 🔀 = the player owns a partner device - one gear swap enables it
  const synergyStatus = isEmpty ? null : getContractorSynergyStatus(member);

  // Build skills display (sorted by level descending)
  const skillsText = isEmpty
    ? ""
    : Object.entries(member.skills)
        .sort(([, a], [, b]) => b - a) // Sort by level descending
        .map(([skill, level]) => {
          const skillIcon = getSkillIcon(skill);
          const skillName = faction.skills[skill]?.name || skill;
          const truncatedName = skillName;

          const syn = synergyStatus?.skills?.[skill];
          let synIcon = "";
          if (syn?.state === "ready") {
            synIcon = `<span class="ml-1 cursor-help" title="Synergy ready: the team's ${getTechniqueName(syn.techniqueId)} gear upgrades ${skillName} events to synergy variants (bonus salvage)">⚡</span>`;
          } else if (syn?.state === "swap") {
            synIcon = `<span class="ml-1 cursor-help opacity-60" title="Synergy possible: equip your ${syn.gearIcon || ''} ${syn.gearName} (${getTechniqueName(syn.techniqueId)}) to upgrade ${skillName} events to synergy variants">🔀</span>`;
          }

          // Determine skill highlighting based on path requirements
          let skillClasses = "flex items-start justify-between text-sm mb-1 px-1";
          let nameClasses = "whitespace-nowrap overflow-hidden tracking-tighter";
          let levelClasses = "font-medium text-game-gold";

          if ((context === "modal" || context === "team") && pathSkills && teamSkills && !isPlayer) {
            const isNeededSkill = pathSkills.has(skill) && (teamSkills[skill] || 0) === 0;
            const isMatchingSkill = pathSkills.has(skill) && (teamSkills[skill] || 0) > 0;

            if (isNeededSkill) {
              // Critical needed skill - keep text highlighting only
              nameClasses += " text-orange-300";
              levelClasses = "font-bold text-orange-400";
            } else if (isMatchingSkill) {
              // Matching skill - keep text highlighting only
              nameClasses += " text-blue-300";
              levelClasses = "font-bold text-blue-400";
            }
          }

          return `<div class="${skillClasses}">
                <span class="flex items-start">
                    <span class="skill-icon">${skillIcon}</span>
                    <span class="${nameClasses}" title="${skillName}">${truncatedName}</span>
                    ${synIcon}
                </span>
                <span class="${levelClasses}">${level}</span>
            </div>`;
        })
        .join("");

  // Equipped skill gear: shown on the card because a contractor's tools
  // unlock synergy variants and success bonuses for the whole team.
  // ⚡ marks gear that partners YOUR skills' events (the reverse direction:
  // hiring this contractor upgrades your own events to synergy variants).
  const equippedGear = isEmpty ? [] : Object.values(member.equippedTechniqueGear || {}).filter(Boolean);
  const gearText = equippedGear.length === 0 ? "" : `
        <div class="border-t border-gray-600 pt-1 mt-1">
            ${equippedGear.map(gear => {
              const partneredSkills = synergyStatus?.gearPartners?.[gear.techniqueId] || [];
              const partnerNames = partneredSkills.map(s => getSkillDisplayName(s)).join(", ");
              const title = partneredSkills.length > 0
                ? `${gear.name} — synergy partner for your ${partnerNames} events (bonus salvage on success)`
                : `${gear.name} — boosts ${getTechniqueName(gear.techniqueId) || 'its technique'} for the team`;
              return `
                <div class="flex items-start text-sm mb-1 px-1 text-amber-300" title="${title}">
                    <span class="skill-icon">⚙️</span>
                    <span class="whitespace-nowrap overflow-hidden tracking-tighter">${gear.name}</span>
                    ${partneredSkills.length > 0 ? '<span class="ml-1 cursor-help">⚡</span>' : ''}
                </div>`;
            }).join("")}
        </div>`;

  // Build health status - ensure all contractors show HP (except empty slots)
  let statusText = "";
  if (!isEmpty && isInjured && member.recoveryTime) {
    const timeLeft = Math.max(0, member.recoveryTime - Date.now());
    const hoursLeft = Math.ceil(timeLeft / (60 * 60 * 1000));
    statusText = `<div class="text-red-500">🏥 ${hoursLeft}h recovery</div>`;
  } else if (!isEmpty) {
    // Initialize HP if missing
    const maxHp = member.maxHp || 100;
    const currentHp = Math.max(0, member.currentHp !== undefined ? member.currentHp : maxHp);
    const healthPercent = (currentHp / maxHp) * 100;
    const healthColor = healthPercent > 75 ? "#4caf50" : healthPercent > 25 ? "#ffd700" : "#ff6b6b";
    statusText = `<div style="color: ${healthColor};">❤️${Math.floor(currentHp)}</div>`;
  }

  // Calculate skill relevance for modal context
  let relevanceText = "";
  let relevanceClass = "";
  if (context === "modal" && pathSkills && teamSkills && !isPlayer) {
    const relevantSkills = Object.keys(member.skills).filter((skill) => pathSkills.has(skill));
    const uncoveredSkills = Object.keys(member.skills).filter((skill) => pathSkills.has(skill) && (teamSkills[skill] || 0) === 0);

    if (uncoveredSkills.length > 0) {
      relevanceText = `<span class="skill-relevance critical">🔥 ${uncoveredSkills.length} needed skill${uncoveredSkills.length > 1 ? "s" : ""}</span>`;
      relevanceClass = "critical-match";
    } else if (relevantSkills.length > 0) {
      relevanceText = `<span class="skill-relevance">🎯 ${relevantSkills.length} matching skill${relevantSkills.length > 1 ? "s" : ""}</span>`;
      relevanceClass = "skill-match";
    }
  }

  // Determine card status styling
  let statusClasses = "bg-white/5 border-2 border-gray-600";
  if (isHired) statusClasses = "bg-green-500/20 border-green-500";
  if (isReserved) statusClasses = "bg-game-gold/20 border-game-gold";
  if (isInjured) statusClasses = "bg-red-500/20 border-red-500";
  if (isPlayer) statusClasses = "bg-game-accent/20 border-game-accent";
  if (relevanceClass === "critical-match") statusClasses = "bg-orange-500/20 border-orange-500";
  else if (relevanceClass === "skill-match") statusClasses = "bg-blue-500/20 border-blue-500";

  // Build action button
  let actionButton = "";
  if (isEmpty && showReserveButton) {
    actionButton = `<button class="bg-blue-400 hover:bg-blue-300 text-black hover:text-gray text-xs px-3 py-1 rounded font-bold" onclick="openContractorModal()">
            Reserve
        </button>`;
  } else if (isEmpty) {
    actionButton = "";
  } else if (isReadOnly) {
    // No action button in read-only mode
    actionButton = "";
  } else if (isMarketplaceContractor && context === "main") {
    // Handle marketplace contractors with reservation system
    const raidInProgress = gameState.currentRaid.hiredContractors && gameState.currentRaid.hiredContractors.length > 0;

    if (isReserved && !raidInProgress) {
      actionButton = `<button class="px-2 py-1 rounded transition-all duration-200 text-xs font-bold bg-red-500 hover:bg-red-600 text-white hover:shadow-lg border border-red-400" onclick="releaseReservedContractor('${member.id}')">Release</button>`;
    } else if (gameState.currentRaid.selectedTier && gameState.currentRaid.currentRaidGrid && !raidInProgress) {
      const disabled = gameState.currentRaid.reservedContractors.length >= 2;
      const buttonText = disabled ? "Team Full" : "Reserve";
      const buttonClasses = disabled
        ? "bg-gray-600 text-gray-400 cursor-not-allowed border border-gray-500"
        : "bg-purple-600 hover:bg-purple-700 text-white hover:shadow-lg border border-purple-400";
      actionButton = `<button class="px-2 py-1 rounded transition-all duration-200 text-xs font-bold ${buttonClasses}" onclick="reserveContractorFromModal('${
        member.id
      }')" ${disabled ? "disabled" : ""}>
                    ${buttonText}
                </button>`;
    }
  } else if (context === "main" && !isPlayer) {
    const raidInProgress = gameState.currentRaid.hiredContractors && gameState.currentRaid.hiredContractors.length > 0;

    if (isHired || (isReserved && !raidInProgress) || (gameState.currentRaid.selectedTier && gameState.currentRaid.currentRaidGrid && !raidInProgress)) {
      const disabled = isInjured || (!isHired && !isReserved && gameState.currentRaid.reservedContractors.length >= 2);
      const buttonText = isHired
        ? "Release"
        : isReserved
        ? "Release"
        : isInjured
        ? "Injured"
        : gameState.currentRaid.reservedContractors.length >= 2
        ? "Team Full"
        : "Reserve";
      const buttonClasses = disabled
        ? "bg-gray-400 text-black text-xs px-3 py-1 rounded font-bold cursor-not-allowed"
        : "bg-blue-400 hover:bg-blue-300 text-black hover:text-gray text-xs px-3 py-1 rounded font-bold";
      actionButton = `<button class="${buttonClasses}" @click="toggleContractor('${member.id}')" ${disabled ? "disabled" : ""}>
                    ${buttonText}
                </button>`;
    }
  } else if (context === "modal" && !isPlayer) {
    const raidInProgress = gameState.currentRaid.hiredContractors && gameState.currentRaid.hiredContractors.length > 0;

    if (gameState.currentRaid.selectedTier && gameState.currentRaid.currentRaidGrid && !raidInProgress) {
      const disabled = gameState.currentRaid.reservedContractors.length >= 2;
      const buttonText = disabled ? "Team Full" : "Reserve";
      const buttonClasses = disabled
        ? "bg-gray-400 text-black text-xs px-3 py-1 rounded font-bold cursor-not-allowed"
        : "bg-blue-400 hover:bg-blue-300 text-black hover:text-gray text-xs px-3 py-1 rounded font-bold";
      actionButton = `<button class="px-2 py-1 rounded transition-all duration-200 text-xs font-bold ${buttonClasses}" onclick="reserveContractorFromModal('${
        member.id
      }')" ${disabled ? "disabled" : ""}>
                    ${buttonText}
                </button>`;
    }
  } else if (context === "team" && !isPlayer && !isHired) {
    // Only show release button for reserved contractors, not hired ones
    actionButton = `<button class="px-2 py-1 rounded transition-all duration-200 text-xs font-bold bg-red-500 hover:bg-red-600 text-white hover:shadow-lg border border-red-400" onclick="releaseReservedContractor('${member.id}')">Release</button>`;
  }

  // Build timer display for reserved marketplace contractors in team view
  let timerDisplay = "";
  if (context === "team" && isMarketplaceContractor && isReserved && member.reservationExpires) {
    const timeLeft = Math.max(0, Math.floor((member.reservationExpires - Date.now()) / 1000));
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    timerDisplay = `<div class="bg-purple-700 text-purple-100 px-2 py-1 rounded text-xs font-bold border border-purple-400" id="reservation-timer-${member.id}">
            ${minutes}:${seconds.toString().padStart(2, "0")}
        </div>`;
  }

  // Build cost/percentage display for bottom left corner
  let costDisplay = "";
  if (isEmpty) {
    costDisplay = `<div class="bg-gray-700 text-gray-500 px-2 py-1 rounded-full text-xs font-bold border border-gray-500 ${
      isEmpty ? "border-dashed" : ""
    }">-%💰</div>`;
  } else if (!isPlayer) {
    costDisplay = `<div class="bg-gray-700 text-gray-100 px-2 py-1 rounded-full text-xs font-bold border border-gray-500">${member.rewardPercentage}%💰</div>`;
  }

  // Build detailed tooltip content for contractor
  const tooltipContent = isPlayer
    ? `Leader of the raid team<br><strong>Faction:</strong> ${faction.name}<br><strong>Skills:</strong><br>${skillsText
        .replace(/class="skill-line"/g, "")
        .replace(/<div>/g, "")
        .replace(/<\/div>/g, "<br>")}`
    : `<strong>Contractor Details</strong><br><strong>Faction:</strong> ${faction.name}<br><strong>Reward Share:</strong> ${
        member.rewardPercentage
      }%💰<br><strong>Skills:</strong><br>${skillsText
        .replace(/class="skill-line"/g, "")
        .replace(/<div>/g, "")
        .replace(/<\/div>/g, "<br>")}`;

  // Determine card colors based on status
  let cardBg, cardBorder, frameBorder, headerBg, headerBorder, headerText;

  if (isPlayer) {
    cardBg = "from-gray-800 to-gray-900";
    cardBorder = "border-purple-600";
    frameBorder = "border-purple-500";
    headerBg = "from-purple-700 to-purple-600";
    headerBorder = "border-purple-500";
    headerText = "text-purple-100";
  } else if (isMarketplaceContractor && isReserved) {
    // Reserved marketplace contractor - purple/amber blend
    cardBg = "from-purple-800 to-purple-900";
    cardBorder = "border-purple-500";
    frameBorder = "border-purple-400";
    headerBg = "from-purple-700 to-purple-600";
    headerBorder = "border-purple-400";
    headerText = "text-purple-100";
  } else if (isMarketplaceContractor) {
    cardBg = "from-indigo-800 to-indigo-900";
    cardBorder = "border-indigo-600";
    frameBorder = "border-indigo-500";
    headerBg = "from-indigo-700 to-indigo-600";
    headerBorder = "border-indigo-500";
    headerText = "text-indigo-100";
  } else if (isHired) {
    cardBg = "from-green-800 to-green-900";
    cardBorder = "border-green-600";
    frameBorder = "border-green-500";
    headerBg = "from-green-700 to-green-600";
    headerBorder = "border-green-500";
    headerText = "text-green-100";
  } else if (isReserved) {
    cardBg = "from-amber-800 to-amber-900";
    cardBorder = "border-amber-600";
    frameBorder = "border-amber-500";
    headerBg = "from-amber-700 to-amber-600";
    headerBorder = "border-amber-500";
    headerText = "text-amber-100";
  } else if (relevanceClass === "critical-match") {
    cardBg = "from-gray-800 to-gray-900";
    cardBorder = "border-orange-700";
    frameBorder = "border-orange-600";
    headerBg = "from-orange-800 to-orange-700";
    headerBorder = "border-orange-600";
    headerText = "text-orange-100";
  } else if (relevanceClass === "skill-match") {
    cardBg = "from-gray-800 to-gray-900";
    cardBorder = "border-blue-600";
    frameBorder = "border-blue-500";
    headerBg = "from-blue-700 to-blue-600";
    headerBorder = "border-blue-500";
    headerText = "text-blue-100";
  } else {
    // Use level-based colors for neutral contractors
    const levelColors = getLevelBasedCardColors(member.level || 1);
    cardBg = levelColors.cardBg;
    cardBorder = isEmpty ? `border-dashed ${levelColors.cardBorder}` : levelColors.cardBorder;
    frameBorder = isEmpty ? `border-dashed ${levelColors.frameBorder}` : levelColors.frameBorder;
    headerBg = levelColors.headerBg;
    headerBorder = isEmpty ? `border-dashed ${levelColors.headerBorder}` : levelColors.headerBorder;
    headerText = "text-white";
  }

  const overlayBg = "bg-black bg-opacity-70";
  const overlayBorder = "border-gray-500";
  const overlayText = "text-gray-400";

  // Create unified card HTML for all contexts
  return `
        <div class="relative bg-gradient-to-b ${cardBg} border-4 ${cardBorder} rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 h-[430px] w-60 overflow-hidden">
            <!-- Card Border Frame -->
            <div class="absolute inset-1 border-2 ${frameBorder} rounded-lg"></div>

            <!-- Header Banner -->
            <div class="relative bg-gradient-to-r ${headerBg} ${headerText} px-3 py-1 text-left border-b-2 ${headerBorder} h-8">
                <div class="absolute top-0.5 right-0.5 bg-black bg-opacity-30 border border-black rounded-full w-6 h-6 flex items-center justify-center text-sm" title="${
                  faction.name
                }">${faction.icon || "🏢"}</div>
            </div>

            <!-- Card Content -->
            <div class="relative p-3 bg-gradient-to-b ${cardBg}" style="height: calc(100% - 32px);">
                <!-- Title Section -->
                <div class="h-4 flex items-center justify-between">
                    <h4 class="font-bold ${isEmpty ? "text-gray-500" : "text-white"} text-sm leading-tight contractor-tooltip" data-tippy-content="${isEmpty ? "" : tooltipContent.replace(/"/g, "&quot;").replace(/'/g, "&#39;")}">
                        ${isEmpty ? "Available Slot" : member.name}
                    </h4>
                    ${!isEmpty ? `<div class="text-xs text-game-gold font-bold">Lv.${member.level || 1}</div>` : ""}
                </div>

                <div class="h-4 mb-2">
                    <h3 class="font-italic text-gray-400 text-xs leading-tight">
                        ${isEmpty ? "" : member.title ? member.title : isPlayer ? "Leader" : "Contractor"}
                    </h3>
                </div>

                <!-- Avatar Area -->
                <div class="relative bg-gradient-to-b from-gray-600 to-gray-900 border-2 ${isEmpty ? "border-dashed border-gray-500" : "border-gray-500"} rounded-lg h-[100px] w-full mb-2 overflow-hidden group cursor-pointer">
                    <!-- Avatar -->
                    <div class="absolute inset-0 flex items-center justify-center">
                        ${
                          isEmpty
                            ? `<div class="contractor-avatar-placeholder contractor-avatar-placeholder transform group-hover:scale-110 transition-transform duration-200"></div>`
                            : `<div class="contractor-avatar-placeholder transform group-hover:scale-110 transition-transform duration-200" data-avatar-nobg="1" data-avatar-seed="${getAvatarSeed(member)}" data-avatar-faction="${member.faction}"></div>`
                        }
                    </div>

                    <!-- Status badges -->
                    ${
                      isHired && !raidCompleted
                        ? '<div class="absolute top-1 left-1 bg-green-600 text-white px-2 py-1 rounded text-xs font-bold">HIRED</div>'
                        : ""
                    }
                    ${
                      isHired && raidCompleted && isMarketplaceContractor && context === "team"
                        ? '<div class="absolute top-1 left-1 bg-blue-600 text-white px-2 py-1 rounded text-xs font-bold">RELEASED</div>'
                        : ""
                    }
                    ${isReserved ? '<div class="absolute top-1 left-1 bg-amber-600 text-white px-2 py-1 rounded text-xs font-bold">RESERVED</div>' : ""}
                    ${
                      isMarketplaceContractor
                        ? '<div class="absolute bottom-1 right-1 bg-indigo-600 text-white px-2 py-1 rounded text-xs font-bold">👤 PLAYER</div>'
                        : ""
                    }
                    ${relevanceText ? `<div class="absolute top-1 right-1 text-s">${relevanceText}</div>` : ""}
                    ${
                      statusText ? `<div class="absolute bottom-1 left-1 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs border border-gray-700">${statusText}</div>` : ""
                    }

                    <!-- Quote Overlay at Bottom (hidden by default, shown on hover) -->
                    <div class="${
                      member.quote ? "" : "hidden"
                    } absolute bottom-0 left-0 right-0 ${overlayBg} p-2 border-t ${overlayBorder} transform translate-y-full group-hover:translate-y-0 transition-transform duration-200 z-10">
                        <p class="${overlayText} text-xs leading-tight">${member.quote ? member.quote : ""}</p>
                    </div>
                </div>

                <!-- Skills Section -->
                <div class="mt-2 text-xs space-y-1 mb-3 bg-gradient-to-b from-gray-800 to-gray-900 border-2 border-gray-500 rounded-lg p-1 ${
                  isEmpty ? "border-dashed" : ""
                }" style="height: 185px; overflow-y: auto;">
                    ${isEmpty ? '<div class="text-gray-600 text-center py-8">No skills</div>' : skillsText}
                    ${gearText}
                </div>

                <!-- Footer Section -->
                <div class="absolute bottom-3 left-3 right-3 flex justify-between items-center gap-1">
                    ${costDisplay}
                    ${timerDisplay}
                    <div class="ml-auto">${actionButton}</div>
                </div>
            </div>
        </div>
    `;
}

// Display contractors (used for non-Alpine.js contexts)
export async function displayContractors() {
  const contractorList = document.getElementById("contractor-list");
  if (!contractorList) return;

  // Get all available contractors (AI + Marketplace)
  const allContractors = await getAllAvailableContractors();

  if (!allContractors.length) {
    contractorList.innerHTML = '<p class="text-gray-500 text-center col-span-full">Create a character first to see available contractors.</p>';
    return;
  }

  contractorList.innerHTML = "";

  allContractors.forEach((contractor) => {
    // Ensure health properties exist and are synchronized
    contractor.maxHp = contractor.maxHp || contractor.max_hp || 100;
    if (contractor.currentHp === undefined) {
      contractor.currentHp = contractor.current_hp || contractor.maxHp;
    }

    const contractorDiv = document.createElement("div");
    contractorDiv.innerHTML = createContractorCard(contractor, "main");
    contractorList.appendChild(contractorDiv.firstElementChild);
  });
}

// Get all available contractors (AI + Marketplace)
export async function getAllAvailableContractors() {
  const contractors = [];

  // Add AI contractors
  if (gameState.contractors && gameState.contractors.length > 0) {
    contractors.push(...gameState.contractors);
  }

  // Add marketplace contractors filtered by selected tier
  try {
    let minLevel = 0;
    let maxLevel = 999;

    if (gameState.currentRaid.selectedTier && config.raidTierData[gameState.currentRaid.selectedTier]) {
      const tier = config.raidTierData[gameState.currentRaid.selectedTier];
      minLevel = tier.contractorMinLevel;
      maxLevel = tier.contractorMaxLevel;
    }

    // A contract must have enough time left to see the raid through. If it
    // expires mid-raid the owner reclaims the character locally while it is
    // still in this raid — conflicting state, and the completion payout is
    // blocked by the in_raid guard. Hide those listings rather than strand them.
    const selectedTier = gameState.currentRaid.selectedTier || 1;
    const requiredMs = expectedRaidOutcome(selectedTier).avgRaidSeconds * 1000;
    const now = Date.now();

    // You can't hire your own characters — exclude every character this player
    // owns, not just the active one (any of them could be listed).
    const ownCharacterIds = (gameState.characters || [])
      .map((c) => c.multiplayerId)
      .filter(Boolean);

    const marketplaceContractors = (await multiplayer.getMarketplaceListings(minLevel, maxLevel, ownCharacterIds))
      ?.filter((c) => {
        const expiresAt = c.expires_at || c.expiresAt;
        if (!expiresAt) return true; // no expiry data — let the server decide
        return new Date(expiresAt).getTime() - now >= requiredMs;
      });

    if (marketplaceContractors && marketplaceContractors.length > 0) {
      contractors.push(
        ...marketplaceContractors.map((c) => ({
          ...c,
          // Normalize database field names (snake_case) to the camelCase the
          // game engine reads. equippedTechniqueGear in particular is what the
          // raid/event engine uses to apply a contractor's techniques.
          currentHp: c.current_hp || c.maxHp || c.max_hp || 100,
          maxHp: c.max_hp || 100,
          rewardPercentage: c.reward_percentage || 15,
          equippedTechniqueGear: c.equippedTechniqueGear || c.equipped_technique_gear || {},
          avatarSeed: c.avatarSeed || c.avatar_seed || null,
          avatarFeatures: c.avatarFeatures || c.avatar_features || null,
        }))
      );
    }
  } catch (error) {
    console.warn("Could not fetch marketplace contractors:", error);
  }

  return contractors;
}

/**
 * Skeleton Crew refuses a crew. Checked at RESERVATION rather than at raid
 * start, so the answer arrives when the player clicks rather than as a surprise
 * on launch — and it is one helper because there are two ways to reserve
 * (the team panel and the contractor modal) and a guard on only one of them is
 * not a guard.
 */
function blackoutBlocksContractors() {
  if (!blackoutModifier('noContractors', false)) return false;
  addNotification("💀 Skeleton Crew: this contract is run alone — no contractors.", "warning");
  return true;
}

// Toggle contractor hire status
export async function toggleContractor(contractorId) {
  const contractor = gameState.contractors.find((c) => c.id == contractorId);
  if (!contractor) return;

  const currentCharacter = gameState.characters?.find((c) => c.id === gameState.activeCharacterId);
  if (!currentCharacter) return;

  if (isContractorHired(contractor.id)) {
    // Release hired contractor (remove from hired list)
    if (contractor.id > 0) {
      await multiplayer.releaseHiredContractor(contractor.id, currentCharacter.multiplayerId);
    }
    gameState.currentRaid.hiredContractors = gameState.currentRaid.hiredContractors.filter((c) => c.id != contractorId);
  } else if (isContractorReserved(contractor.id)) {
    // Release reserved contractor (remove from reserved list)
    if (contractor.id > 0) {
      await multiplayer.releaseContractor(contractor.id, currentCharacter.multiplayerId);
    }
    gameState.currentRaid.reservedContractors = gameState.currentRaid.reservedContractors.filter((c) => c.id != contractorId);
  } else {
    if (blackoutBlocksContractors()) return;
    // Check team size limit (player + 2 contractors max)
    if (gameState.currentRaid.reservedContractors.length >= 2) {
      addNotification("⚠️ Team is full! Maximum 3 members (you + 2 contractors).", "warning");
      return;
    }

    // Try to reserve contractor in database first (for multiplayer contractors)
    if (contractor.multiplayerId) {
      const reservationResult = await multiplayer.reserveContractor(contractor.multiplayerId, currentCharacter.multiplayerId);
      if (!reservationResult.success) {
        addNotification(`❌ ${reservationResult.message}`, "error");
        return;
      }
    }

    // Reserve contractor (add to reserved list - no cost yet)
    gameState.currentRaid.reservedContractors.push(contractor);
  }

  // Update Alpine store
  Alpine.store("contractors").refresh();

  updateGridTeamDisplay(); // Update raid team display
  updateGridDisplay();
  updatePathDisplay(); // Update current path skill coverage
  updateUI();
  saveGameState();
}

export function openContractorModal() {
  const modal = document.getElementById("contractor-modal");
  modal.classList.remove("hidden");
  // Add entrance animation
  modal.classList.add("animate__animated", "animate__fadeIn");
  modal.querySelector(".modal-content").classList.add("animate__animated", "animate__zoomIn");
  // Alpine.js now handles the contractor display reactively
}

export function closeContractorModal() {
  const modal = document.getElementById("contractor-modal");
  const modalContent = modal.querySelector(".modal-content");

  // Add exit animation
  modal.classList.add("animate__animated", "animate__fadeOut");
  modalContent.classList.add("animate__animated", "animate__zoomOut");

  // Hide modal after animation completes
  setTimeout(() => {
    modal.classList.add("hidden");
    // Clean up animation classes
    modal.classList.remove("animate__animated", "animate__fadeIn", "animate__fadeOut");
    modalContent.classList.remove("animate__animated", "animate__zoomIn", "animate__zoomOut");
  }, 150);
}

export function calculateContractorPriority(contractor, pathSkills, teamSkills) {
  const contractorSkills = Object.keys(contractor.skills);

  // Count skills that are needed for the path
  const totalNeededSkills = contractorSkills.filter((skill) => pathSkills.has(skill)).length;

  // Count skills that are needed but not covered by current team
  const uncoveredNeededSkills = contractorSkills.filter((skill) => pathSkills.has(skill) && (teamSkills[skill] || 0) === 0).length;

  return {
    uncoveredNeededSkills,
    totalNeededSkills,
  };
}

export async function reserveContractorFromModal(contractorId) {
  // Find contractor in Alpine store (includes both AI and marketplace contractors)
  //const contractor = Alpine.store('contractors').all.find(c => c.id == contractorId);
  const contractor = gameState.contractors.find((c) => c.id == contractorId);

  // console.log('Reserving contractor from modal:', contractorId, contractor, isContractorReserved(contractorId));

  if (!contractor || isContractorReserved(contractorId)) return;
  if (blackoutBlocksContractors()) return;

  const currentCharacter = gameState.characters?.find((c) => c.id === gameState.activeCharacterId);
  if (!currentCharacter) return;

  // Check team size limit (player + 2 contractors max)
  if (gameState.currentRaid.reservedContractors.length >= 2) {
    addNotification("⚠️ Team is full! Maximum 3 members (you + 2 contractors).", "warning");
    return;
  }

  // Handle marketplace contractors (positive ID) vs AI contractors (negative ID)
  if (contractor.id > 0) {
    // Marketplace contractor - reserve via server
    const reservationResult = await multiplayer.reserveContractor(contractor.id, currentCharacter.multiplayerId);
    if (reservationResult.success) {
      // Add marketplace contractor with expiration time from backend
      gameState.currentRaid.reservedContractors.push({
        ...contractor,
        reservedAt: Date.now(),
        reservationExpires: new Date(reservationResult.expiresAt).getTime(),
      });
    } else {
      addNotification(`❌ ${reservationResult.message}`, "error");
      return;
    }
  } else {
    // AI contractor (negative ID) - no server reservation needed
    gameState.currentRaid.reservedContractors.push(contractor);
  }

  // Update Alpine store
  if (Alpine.store("contractors")) {
    Alpine.store("contractors").updateReserved(gameState.currentRaid.reservedContractors);
  }

  updateGridTeamDisplay();
  updateGridDisplay();
  updatePathDisplay(); // Update current path skill coverage
  updateUI();
  saveGameState();

  // Auto-close modal if team is now full
  if (gameState.currentRaid.reservedContractors.length >= 2) {
    setTimeout(() => {
      closeContractorModal();
    }, 500); // Small delay to let user see the action complete
  }
}

// Health regeneration and contractor recovery system
export function processTeamRegeneration(timePassedMs) {
  const raidTeam = getRaidTeam();
  let hasChanges = false;
  raidTeam.forEach((member) => {
    hasChanges = hasChanges || processCharacterHealthRegeneration(member, timePassedMs);
  });
  return hasChanges;
}

// Check for and remove expired marketplace contractor reservations
export function checkExpiredReservations() {
  const now = Date.now();
  const expiredContractors = [];

  // Find expired marketplace contractor reservations
  gameState.currentRaid.reservedContractors.forEach((contractor) => {
    if (contractor.id > 0 && contractor.reservationExpires && contractor.reservationExpires < now) {
      expiredContractors.push(contractor);
    }
  });

  if (expiredContractors.length > 0) {
    // Remove expired reservations
    expiredContractors.forEach((contractor) => {
      // console.log(`Reservation expired for marketplace contractor: ${contractor.name}`);
      gameState.currentRaid.reservedContractors = gameState.currentRaid.reservedContractors.filter((c) => c.id !== contractor.id);
    });

    // Update displays
    if (Alpine.store("contractors")) {
      Alpine.store("contractors").updateReserved(gameState.currentRaid.reservedContractors);
    }

    updateGridTeamDisplay();
    updateGridDisplay();
    updateUI();
    saveGameState();

    addNotification(`${expiredContractors.length} contractor reservation(s) expired`, "info");
    return true;
  } else {
    return false;
  }
}

// Update reservation timers on team cards
export function updateReservationTimers() {
  const now = Date.now();

  gameState.currentRaid.reservedContractors.forEach((contractor) => {
    if (contractor.id > 0 && contractor.reservationExpires) {
      const timerElement = document.getElementById(`reservation-timer-${contractor.id}`);
      if (timerElement) {
        const timeLeft = Math.max(0, Math.floor((contractor.reservationExpires - now) / 1000));
        const minutes = Math.floor(timeLeft / 60);
        const seconds = timeLeft % 60;
        timerElement.textContent = `${minutes}:${seconds.toString().padStart(2, "0")}`;
      }
    }
  });
}